import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaLock, FaLockOpen, FaSort } from 'react-icons/fa6';
import { MdPhotoLibrary } from 'react-icons/md';
import classNames from 'classnames';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import SearchBar from '../../components/forms/SearchBar';
import DropdownSelect from '../../components/forms/DropdownSelect';
import photoAlbumService from '../../../services/photoAlbumService';
import ApiImage from '../../components/misc/ApiImage';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { useDebounceEffect } from '../../../utils/useDebounceEffect';

interface PhotoAlbumCardProps {
  album: PhotoAlbum;
  onClick: () => void;
}

export default function PhotoAlbumsOverviewPage(): JSX.Element {
  const navigate = useNavigate();
  const [photoAlbums, setPhotoAlbums] = React.useState<PhotoAlbum[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'date' | 'photoCount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const business = useSelectedBusiness();

  const sortOptions = [
    { label: t('page.albums.sort.date'), value: 'date' as const },
    { label: t('page.albums.sort.title'), value: 'title' as const },
    { label: t('page.albums.sort.photoCount'), value: 'photoCount' as const },
  ];

  const orderOptions = [
    { label: t('page.albums.sort.desc'), value: 'desc' as const },
    { label: t('page.albums.sort.asc'), value: 'asc' as const },
  ];

  const fetchAlbums = async () => {
    if (!business?.businessId) return;
    setIsLoading(true);
    try {
      const albums = await photoAlbumService.getPhotoAlbums(business.businessId, {
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setPhotoAlbums(albums);
    } catch (error) {
      console.error('Failed to fetch photo albums:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useDebounceEffect(
    () => {
      fetchAlbums();
    },
    300,
    [business?.businessId, search, sortBy, sortOrder],
  );

  const handleAlbumClick = (albumId: string) => {
    navigate(`/album/${albumId}`);
  };

  return (
    <>
      <PageHeader
        title={t('page.albums.title')}
        subtitle={t('page.albums.subtitle')}
        primaryButton={
          <PrimaryButton
            text={t('page.albums.createAlbum')}
            icon={FaPlus}
            onClick={() => navigate('/create-album')}
          />
        }
      />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          search={search}
          setSearch={setSearch}
          className="w-full sm:w-80"
          inputProps={{
            placeholder: t('page.albums.search.placeholder'),
          }}
        />

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <FaSort className="h-4 w-4 text-primary-900" />
            <span className="text-sm font-medium text-primary-900">
              {t('page.albums.sort.label')}:
            </span>
          </div>

          <DropdownSelect
            options={sortOptions}
            value={sortBy}
            setValue={(value) => value && setSortBy(value)}
            placeholder={t('page.albums.sort.sortBy')}
          />

          <DropdownSelect
            options={orderOptions}
            value={sortOrder}
            setValue={(value) => value && setSortOrder(value)}
            placeholder={t('page.albums.sort.order')}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-primary-900">{t('general.loading')}...</div>
        </div>
      ) : photoAlbums.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {photoAlbums.map((album) => (
            <PhotoAlbumCard
              key={album.id}
              album={album}
              onClick={() => handleAlbumClick(album.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyScreenView
          title={search ? t('page.albums.empty.noResults') : t('page.albums.empty.title')}
          subtitle={
            search ? t('page.albums.empty.tryDifferentSearch') : t('page.albums.empty.subtitle')
          }
        />
      )}
    </>
  );
}

function PhotoAlbumCard({ album, onClick }: PhotoAlbumCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer rounded-xl bg-secondary-200 p-4 transition-all hover:bg-opacity-80 hover:shadow-md">
      <div className="relative mb-4 flex h-40 w-full items-center justify-center overflow-hidden rounded-lg bg-primary-300 bg-opacity-20">
        <ApiImage
          path={album.coverUrl || '/assets/vera_smile.svg'}
          alt={album.title}
          className={classNames('h-full w-full transition-opacity group-hover:opacity-80', {
            'object-cover': album.coverUrl,
          })}
        />
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-primary-900 bg-opacity-80 px-2 py-1 text-xs text-secondary-50">
          <MdPhotoLibrary className="h-3 w-3" />
          <span className="text-xs text-secondary-50">{album.photoCount}</span>
        </div>
      </div>

      <div className="space-y-1">
        <h3 className="line-clamp-1 text-lg font-semibold text-primary-900">{album.title}</h3>
        <p className="line-clamp-2 text-sm text-gray-600">{album.description}</p>

        <div className="flex items-center justify-between text-xs">
          <div
            className={`flex items-center gap-1 ${album.openToUpload ? 'text-success' : 'text-secondary'}`}>
            {album.openToUpload ? (
              <>
                <FaLockOpen className="h-3 w-3" />
                <span>{t('page.albums.openUpload')}</span>
              </>
            ) : (
              <>
                <FaLock className="h-3 w-3" />
                <span>{t('page.albums.restrictedUpload')}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
